import type { WeldTelemetrySampleRow } from './weldTelemetryClient'
import { splitSamplesByDirectionPass } from './passAnalyticsGraphAxis'

/**
 * Resolve sample IDs whose evt_no falls in an inclusive event-ID range.
 * Range endpoints may be given in either order.
 */
export function sampleIdsInEventRange(
  samples: Array<{ sampleId: number; evtNo: number }>,
  eventFrom: number,
  eventTo: number
): number[] {
  if (!Number.isFinite(eventFrom) || !Number.isFinite(eventTo)) return []
  const lo = Math.min(eventFrom, eventTo)
  const hi = Math.max(eventFrom, eventTo)
  return samples
    .filter((s) => Number.isFinite(s.evtNo) && s.evtNo >= lo && s.evtNo <= hi)
    .map((s) => s.sampleId)
}

/**
 * Default segment for "Move to pass" when the user has not picked one.
 * Prefers an unknown "Segment N" bucket when present; otherwise the last
 * series (typically CCW when CW+CCW are both plotted).
 */
export function defaultExtraSegmentLabel(segments: { legendLabel: string }[]): string {
  if (segments.length === 0) return ''
  const unknown = segments.find((s) => /^Segment \d+$/.test(s.legendLabel))
  if (unknown) return unknown.legendLabel
  return segments[segments.length - 1].legendLabel
}

/**
 * Distinct event IDs on this pass with display labels that include the
 * owning graph segment, e.g. value "396" → label "396 (CW)".
 */
export function eventOptionsWithSegmentLabels(
  samples: WeldTelemetrySampleRow[],
  sourceFilenames?: string[]
): { value: string; label: string }[] {
  const segments = splitSamplesByDirectionPass(samples, sourceFilenames)
  const segmentByEvt = new Map<number, string>()
  for (const segment of segments) {
    for (const sample of segment.samples) {
      if (typeof sample.evtNo === 'number' && Number.isFinite(sample.evtNo)) {
        if (!segmentByEvt.has(sample.evtNo)) {
          segmentByEvt.set(sample.evtNo, segment.legendLabel)
        }
      }
    }
  }

  const evts = Array.from(segmentByEvt.keys()).sort((a, b) => a - b)
  return evts.map((evt) => {
    const segment = segmentByEvt.get(evt)
    const value = String(evt)
    return {
      value,
      label: segment ? `${evt} (${segment})` : value
    }
  })
}
